import {
  WorkOrderTaskStatus,
  WorkOrderTaskSubStatus,
} from '@xbcb/work-order-types';
import { getRecordType } from '@xbcb/core';
import { isStartStopTaskButtonHidden } from 'libs/isStartStopTaskButtonHidden';
import { isCuDeEditingEnabled } from 'featureFlags';
import { fetchLatestRecordAndExecuteMutation } from 'libs/fetchLatestRecordAndExecuteMutation';
import { AppRecordSubmitButton } from 'routes';
import { RecordType } from '@xbcb/shared-types';
import { safeGetMessage } from '@xbcb/ui-utils';
import { updateWorkOrderTaskStatus } from 'libs/sharedQueries';
import {
  findWorkOrderTaskByIdOrType,
  WorkOrderTaskWithType,
} from 'libs/findMatchingWorkOrderTask';
import { WorkOrderTaskEvaluatedState } from 'libs/evaluateWorkOrderTaskState';

interface UpdateWorkOrderTaskSubStatusProps {
  updatedRecord: { tasks?: WorkOrderTaskWithType[] } | undefined;
  workOrderTaskType?: string;
  workOrderTaskId?: string;
  subStatus: WorkOrderTaskSubStatus;
  successMessage: string;
}

const updateWorkOrderTaskSubStatus = async ({
  updatedRecord,
  workOrderTaskType,
  workOrderTaskId,
  subStatus,
  successMessage,
}: UpdateWorkOrderTaskSubStatusProps): Promise<void> => {
  const workOrderTask = findWorkOrderTaskByIdOrType(updatedRecord, {
    taskId: workOrderTaskId,
    taskType: workOrderTaskType,
  });
  if (!workOrderTask) return;
  await fetchLatestRecordAndExecuteMutation({
    id: workOrderTask.id,
    fields: 'status',
    recordType: RecordType.WORK_ORDER_TASK,
    mutation: updateWorkOrderTaskStatus,
    successMessage,
    constructVariablesWithIdVersion: (id, version) => ({
      id,
      version,
      input: {
        status: WorkOrderTaskStatus.IN_PROGRESS,
        subStatus,
      },
    }),
  });
};

export const startWorkOrderTaskButton = (): AppRecordSubmitButton => {
  const workOrderButton: AppRecordSubmitButton = {
    key: 'openTask',
    text: ({ appRecordBundle }) => safeGetMessage(appRecordBundle, 'open_task'),
    show: ({ workOrderTaskEvaluatedState, existingRecord }) => {
      return (
        !isStartStopTaskButtonHidden({
          recordType: getRecordType(existingRecord.id),
          isCuDeEditableStage: isCuDeEditingEnabled,
        }) &&
        workOrderTaskEvaluatedState ===
          WorkOrderTaskEvaluatedState.READY_TO_START
      );
    },
    skipValidation: () => true,
    skipUpdateRecord: () => true,
    onSubmit: ({ updatedRecord, workOrderTaskType, workOrderTaskId }) =>
      updateWorkOrderTaskSubStatus({
        updatedRecord,
        workOrderTaskType,
        workOrderTaskId,
        subStatus: WorkOrderTaskSubStatus.ACTIVELY_IN_PROGRESS,
        successMessage: `Work Order Task is now actively in progress`,
      }),
  };
  return workOrderButton;
};

export const stopWorkOrderTaskButton = (): AppRecordSubmitButton => {
  const workOrderButton: AppRecordSubmitButton = {
    key: 'pauseTask',
    text: ({ appRecordBundle }) =>
      safeGetMessage(appRecordBundle, 'pause_task'),
    show: ({ workOrderTaskEvaluatedState, existingRecord }) => {
      return (
        !isStartStopTaskButtonHidden({
          recordType: getRecordType(existingRecord.id),
          isCuDeEditableStage: isCuDeEditingEnabled,
        }) &&
        workOrderTaskEvaluatedState === WorkOrderTaskEvaluatedState.STARTED
      );
    },
    skipValidation: () => true,
    skipUpdateRecord: () => true,
    onSubmit: ({ updatedRecord, workOrderTaskType, workOrderTaskId }) =>
      updateWorkOrderTaskSubStatus({
        updatedRecord,
        workOrderTaskType,
        workOrderTaskId,
        subStatus: WorkOrderTaskSubStatus.PASSIVELY_IN_PROGRESS,
        successMessage: `Work Order Task is no longer actively in progress`,
      }),
  };
  return workOrderButton;
};
